import * as React from 'react'
import { useMemo } from 'react'

import { Button, InputSelect, InputText, Modal } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { Divider, Form } from 'antd'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import { showToast } from '../../../../../common/utils'
import { ResidentNeed } from '@lmc/ts-common'
import { ProfileNeedOption } from '../ResidentNeedsScreen'
import { fetchAllNeeds, NEEDS_QUERY_KEYS, NeedItem } from '../../../../../hooks/useNeeds'
import { NeedCategoryDescriptionEnum, type NeedCategoryCode } from '@lmc/validations'
import { ResidentNeedV6 } from '../../../../../hooks/useResidentNeeds'
import { SETTINGS } from '../../../../../components/routes/settings'
import styled from 'styled-components'

const UNCATEGORIZED = 'Uncategorized'

type NeedsModalProps = {
  isModalVisible: boolean
  handleToogleDeleteModal?: () => void
  handleToggleModal?: () => void
  /** When provided, the modal uses these options for the dropdown instead of fetching from the v6 API (legacy flow). */
  profileNeedOptions?: ProfileNeedOption[]
  profileNeedDetail?: ResidentNeed
  /** @deprecated – care plan blocking is removed; kept for backward compatibility */
  carePlanNominative?: string
  addNeed?: (need: ResidentNeed, id: string) => Promise<unknown>
  updateNeed?: (
    need: ResidentNeed,
    id: string,
    needId: string
  ) => Promise<unknown>
  residentId?: string
  needs?: ResidentNeed[]
  testId?: string
  // ── v6 props ──────────────────────────────────────────────────────
  /** v6 need being edited; when set the modal runs in v6-edit mode */
  residentNeedV6?: ResidentNeedV6 | null
  /** v6 assign callback – used in add mode */
  onV6Assign?: (payload: { needId: string; notes?: string }) => Promise<unknown>
  /** v6 update callback – used in edit mode */
  onV6Update?: (payload: { notes?: string }) => Promise<unknown>
  /** loading state from v6 mutations */
  isV6Loading?: boolean
  /** pre-computed need IDs already assigned to the resident (for v6 flow) */
  assignedV6NeedIds?: string[]
}

export const NeedsModal = ({
  handleToggleModal,
  handleToogleDeleteModal,
  profileNeedOptions,
  isModalVisible,
  profileNeedDetail,
  addNeed,
  updateNeed,
  residentId,
  needs,
  testId,
  residentNeedV6,
  onV6Assign,
  onV6Update,
  isV6Loading,
  assignedV6NeedIds,
}: NeedsModalProps): React.ReactElement => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [isDuplicateNeed, setIsDuplicateNeed] = useState(false)

  // v6 edit mode: either the v6 prop or the legacy prop drives edit
  const isEditMode = !!profileNeedDetail || !!residentNeedV6

  // Legacy mode: profileNeedOptions are provided by the caller — skip v6 fetch
  const isLegacyMode = !!profileNeedOptions

  // Fetch all available needs for the dropdown (v6 endpoint) — only in v6 add mode
  const { data: allNeeds = [] } = useQuery<NeedItem[]>(
    [NEEDS_QUERY_KEYS.options],
    fetchAllNeeds,
    { retry: false, retryOnMount: false, enabled: isModalVisible && !isEditMode && !isLegacyMode }
  )

  // IDs of already-assigned needs — used to disable options in add mode
  // assignedV6NeedIds takes priority (v6 flow); falls back to legacy needs array
  const assignedNeedIds = useMemo(
    () => assignedV6NeedIds ?? needs?.map((n) => n.need) ?? [],
    [assignedV6NeedIds, needs]
  )

  // Group needs by modsCategory for the grouped dropdown
  const groupedNeedOptions = useMemo(() => {
    // Legacy mode: derive options from profileNeedOptions to preserve legacy need IDs
    if (isLegacyMode) {
      return (profileNeedOptions ?? []).map((category) => ({
        label: category.name,
        options: category.needs?.map((need) => ({
          label: need.name,
          value: need.id,
        })) ?? [],
      }))
    }

    // V6 mode: use needs fetched from the v6 endpoint, grouped by modsCategory
    const groups: Record<string, { label: string; value: string; disabled?: boolean }[]> = {}

    allNeeds.forEach((need) => {
      const categoryCode = need.modsCategory as NeedCategoryCode | undefined
      const groupLabel = categoryCode
        ? NeedCategoryDescriptionEnum[categoryCode] ?? categoryCode
        : UNCATEGORIZED

      if (!groups[groupLabel]) {
        groups[groupLabel] = []
      }

      groups[groupLabel].push({
        label: need.name,
        value: need.id,
      })
    })

    return Object.entries(groups)
      .sort(([a], [b]) => {
        if (a === UNCATEGORIZED) return 1
        if (b === UNCATEGORIZED) return -1
        return a.localeCompare(b)
      })
      .map(([label, options]) => ({ label, options }))
  }, [allNeeds, isLegacyMode, profileNeedOptions])

  const onConfirm = () => form.submit()

  const handleCloseForm = () => {
    handleToggleModal?.()
    handleCleanForm()
  }

  const handleCleanForm = () => {
    form.resetFields()
    form.setFieldsValue({})
    setLoading(false)
    setIsDuplicateNeed(false)
  }

  // Populate form when opening in edit mode (legacy or v6)
  useEffect(() => {
    if (residentNeedV6) {
      form.setFieldsValue({ notes: residentNeedV6.notes ?? '' })
    } else if (profileNeedDetail) {
      form.setFieldsValue(profileNeedDetail)
      handleToggleModal?.()
    } else {
      handleCleanForm()
    }
  }, [profileNeedDetail, residentNeedV6])

  const onFinish = async (values: { need: string; notes: string }) => {
    setLoading(true)

    try {
      // ── v6 paths ──────────────────────────────────────────────────
      if (residentNeedV6 && onV6Update) {
        await onV6Update({ notes: values.notes })
        handleCloseForm()
        setLoading(false)
        return
      }

      if (!isEditMode && onV6Assign) {
        await onV6Assign({ needId: values.need, notes: values.notes })
        handleCloseForm()
        setLoading(false)
        return
      }

      // ── legacy paths ───────────────────────────────────────────────
      let res
      if (profileNeedDetail) {
        res = await updateNeed?.(
          { need: profileNeedDetail.need, notes: values.notes } as unknown as ResidentNeed,
          residentId as string,
          profileNeedDetail.id
        )
      } else {
        res = await addNeed?.(
          { need: values.need, notes: values.notes } as unknown as ResidentNeed,
          residentId as string
        )
      }

      if (res) {
        showToast({
          title: profileNeedDetail
            ? t('skillsAndNeeds.changesSaved')
            : t('skillsAndNeeds.needAdded'),
        })
        handleCloseForm()
      }
      setLoading(false)
    } catch (e) {
      setLoading(false)
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const modalTitle = isEditMode
    ? t('skillsAndNeeds.editAssignNeed')
    : t('skillsAndNeeds.assignNewNeed')

  const isSubmitting = isV6Loading || loading

  // Show delete button only in legacy mode
  const deleteFooterElement = !!profileNeedDetail && !residentNeedV6 && handleToogleDeleteModal
    ? (
      <Button variant="danger" onClick={handleToogleDeleteModal}>
        {t('skillsAndNeeds.deleteNeed')}
      </Button>
    )
    : undefined

  return (
    <StyledModal
      localisation={{
        title: modalTitle,
        confirmText: isEditMode ? t('skillsAndNeeds.saveChanges') : t(`general.buttons.confirm`),
        cancelText: t(`general.buttons.cancel`),
      }}
      onConfirm={onConfirm}
      onCancel={handleCloseForm}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isSubmitting, variant: 'primary', disabled: isDuplicateNeed }}
      extraFooterElement={deleteFooterElement}
      data-testid={testId ? `${testId}-needs-modal` : 'needs-modal'}

    >
      <Divider style={{ margin: '0 0 16px 0' }} />
      <Form
        form={form}
        onFinish={onFinish}
        data-testid={testId ? `${testId}-needs-modal-form` : 'needs-modal-form'}
      >
        {/* v6 edit mode: need name shown as read-only text */}
        {residentNeedV6 ? (
          <ReadOnlyField>
            <ReadOnlyLabel>{t('skillsAndNeeds.form.selectNeed')}</ReadOnlyLabel>
            <ReadOnlyValue data-testid={testId ? `${testId}-needs-modal-name` : 'needs-modal-name'}>
              {residentNeedV6.need.name}
            </ReadOnlyValue>
          </ReadOnlyField>
        ) : (
          <InputSelect
            testId={testId ? `${testId}-needs-modal-select` : 'needs-modal-select'}
            errorMessage={t('skillsAndNeeds.form.needRequired')}
            type="need"
            label={t('skillsAndNeeds.form.selectNeed')}
            helperText={t('skillsAndNeeds.form.selectNeedTooltip')}
            layout="column"
            placeholder={t('skillsAndNeeds.form.selectNeedPlaceholder')}
            groupedOptions={groupedNeedOptions}
            required
            disabled={!!profileNeedDetail}
            onChange={(value) => {
              const selectedId = Array.isArray(value) ? value[0] : value
              form.setFields([{ name: 'need', value: selectedId }])
              const isDuplicate = !!(selectedId && assignedNeedIds.includes(selectedId))
              setIsDuplicateNeed(isDuplicate)
              if (isDuplicate) {
                setTimeout(() => {
                  form.setFields([{ name: 'need', errors: [t('skillsAndNeeds.form.needAlreadyAssigned')] }])
                }, 0)
              } else {
                setTimeout(() => {
                  form.setFields([{ name: 'need', errors: [] }])
                }, 0)
              }
            }}
            notFoundContent={
              <NeedTemplatesEmptyState>
                <NeedTemplatesEmptyTitle>{t('skillsAndNeeds.form.noNeedTemplatesTitle')}</NeedTemplatesEmptyTitle>
                <NeedTemplatesEmptySubtitle>{t('skillsAndNeeds.form.noNeedTemplatesSubtitle')}</NeedTemplatesEmptySubtitle>
                <NeedTemplatesLink to={`/${SETTINGS.NEED_TEMPLATES}`} target="_blank" rel="noreferrer">
                  {t('skillsAndNeeds.form.noNeedTemplatesLink')} &rsaquo;
                </NeedTemplatesLink>
              </NeedTemplatesEmptyState>
            }
            data-testid={
              testId ? `${testId}-needs-modal-select` : 'needs-modal-select'
            }
          />
        )}
        <InputText
          type="notes"
          label={t('skillsAndNeeds.form.notes')}
          layout="column"
          element="textarea"
          rows={4}
          style={{ width: '100%' }}
          data-testid={
            testId ? `${testId}-needs-modal-notes` : 'needs-modal-notes'
          }
        />
      </Form>
      <Divider style={{ margin: '8px 0 0 0' }} />
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-form-item-label > label {
    font-weight: 500 !important;
    font-size: 15px !important;
  }

  .ant-form-item-label > label span {
    font-size: 15px !important;
  }

  .ant-form-item-explain-error {
    font-size: 15px !important;
  }

  .ant-select {
    height: 40px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`

const ReadOnlyField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
`

const ReadOnlyLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const ReadOnlyValue = styled.span`
  font-size: 15px;
  font-weight: 400;
  color: ${({ theme }) => theme.color.neutral.color1};
`

export default NeedsModal

const NeedTemplatesEmptyState = styled.div`
  padding: 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const NeedTemplatesEmptyTitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const NeedTemplatesEmptySubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const NeedTemplatesLink = styled(Link)`
  font-size: 14px;
  color: ${({ theme }) => theme.color.status.primary};
  text-decoration: underline;
  margin-top: 4px;

  &:hover {
    color: ${({ theme }) => theme.color.status.primary};
    opacity: 0.8;
  }
`
